import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { layoutGraph } from './graph/graphLayout';

/**
 * WitnessGraphCard — typed chat card for the `show_witness_graph` tool.
 *
 * Renders the tool's subgraph envelope as a small static SVG network (same
 * force layout as the canvas, rasterized once — no React Flow in the chat
 * bundle) with an "Open full graph" deep link into the real Graph Explorer
 * (`?graph=<seed>`; GraphDeepLink owns the overlay on every case tab).
 */

const NODE_COLOR = {
  witness: '#38b6ff',
  entity: '#8a94ad',
  event: '#8a77c9',
  stack: '#8a77c9',
};
const EDGE_COLOR = {
  relationship: '#7c8db5',
  claim: '#c9973e',
  participation: '#6a5aa8',
  location: '#5f9c8a',
  tension: '#e05252',
};

const MAX_NODES = 40;
const W = 620;
const H = 340;

export default function WitnessGraphCard({ result }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const view = useMemo(() => {
    if (!result || result.error || !result.nodes?.length) return null;
    let nodes = result.nodes;
    if (nodes.length > MAX_NODES) {
      nodes = [
        ...nodes.filter((n) => n.is_seed || n.kind === 'witness'),
        ...nodes.filter((n) => !n.is_seed && n.kind !== 'witness'),
      ].slice(0, MAX_NODES);
    }
    const ids = new Set(nodes.map((n) => n.id));
    const edges = (result.edges || []).filter((e) => ids.has(e.source) && ids.has(e.target));
    const pos = layoutGraph(nodes, edges, {}, result.seed?.node_id);
    const xs = Object.values(pos).map((p) => p.x);
    const ys = Object.values(pos).map((p) => p.y);
    const minX = Math.min(...xs); const maxX = Math.max(...xs);
    const minY = Math.min(...ys); const maxY = Math.max(...ys);
    const pad = 34;
    const sx = (W - 2 * pad) / Math.max(maxX - minX, 1);
    const sy = (H - 2 * pad) / Math.max(maxY - minY, 1);
    const s = Math.min(sx, sy, 1.4);
    const px = (id) => ({
      x: pad + (pos[id].x - minX) * s + (W - 2 * pad - (maxX - minX) * s) / 2,
      y: pad + (pos[id].y - minY) * s + (H - 2 * pad - (maxY - minY) * s) / 2,
    });
    return { nodes, edges, px, dropped: (result.nodes.length - nodes.length) };
  }, [result]);

  if (!view) return null;
  const seedId = result.seed?.node_id;

  return (
    <div className="wgc-card">
      <div className="wgc-head">
        <span className="wgc-title">🕸 {result.seed?.label || 'Evidence network'}</span>
        <span className="wgc-counts">
          {result.counts?.nodes ?? view.nodes.length} nodes · {result.counts?.edges ?? view.edges.length} connections
          {view.dropped > 0 ? ` · showing ${view.nodes.length}` : ''}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="wgc-svg" role="img" aria-label="Evidence network preview">
        {view.edges.map((e, i) => {
          const a = view.px(e.source); const b = view.px(e.target);
          return (
            <line
              key={i}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={EDGE_COLOR[e.kind] || '#7c8db5'}
              strokeWidth={e.kind === 'tension' ? 1.6 : 1}
              strokeDasharray={e.kind === 'claim' ? '5 3' : e.kind === 'participation' ? '2 4' : e.kind === 'tension' ? '3 3' : undefined}
              opacity={0.55}
            />
          );
        })}
        {view.nodes.map((n) => {
          const p = view.px(n.id);
          const r = n.id === seedId ? 9 : n.kind === 'witness' ? 6 : 4;
          return (
            <g key={n.id}>
              <circle
                cx={p.x} cy={p.y} r={r}
                fill={NODE_COLOR[n.kind] || '#8a94ad'}
                stroke={n.id === seedId ? '#ffffff' : 'rgba(11,16,32,0.9)'}
                strokeWidth={n.id === seedId ? 2 : 1}
              />
              {(n.id === seedId || n.kind === 'witness') && (
                <text x={p.x} y={p.y - r - 4} textAnchor="middle" className="wgc-label">
                  {(n.label || '').slice(0, 22)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="wgc-foot">
        <button
          className="btn btn-sm btn-primary"
          onClick={() => seedId && navigate(`${pathname}?graph=${seedId}`)}
        >Open full graph →</button>
        <span className="wgc-hint">every connection cites its source document</span>
      </div>
    </div>
  );
}
